import {
  getAssetPrecisions,
  getPrecisedNumber,
  getTxAmount,
  getUsdAmount,
} from "@/helpers";

import styles from "./BalanceInfo.module.sass";

export type BalanceData = {
  asset: string;
  network: string;
  addressDetailsData?: any;
};

interface BalanceInfoProps {
  data: BalanceData;
}

const BalanceInfo = ({ data }: BalanceInfoProps) => {
  const { asset, network, addressDetailsData } = data;

  const hasBalanceInfo = () => {
    return (
      addressDetailsData?.balance_usd ||
      addressDetailsData?.balance ||
      addressDetailsData?.balance_approximate
    );
  };

  const [precision, viewPrecision] = getAssetPrecisions(asset, network);
  const balancePrecision = Object.prototype.hasOwnProperty.call(
    addressDetailsData,
    "reported_balance_precision",
  )
    ? (addressDetailsData.reported_balance_precision ?? precision)
    : precision;

  const balanceUsd = addressDetailsData.balance_usd
    ? getUsdAmount(addressDetailsData.balance_usd)
    : "";
  const balanceAsset =
    getPrecisedNumber(addressDetailsData.balance_approximate, viewPrecision) ||
    (addressDetailsData.balance
      ? getTxAmount(addressDetailsData.balance, balancePrecision, viewPrecision)
      : "");

  const receivedUsd = addressDetailsData.received_usd
    ? getUsdAmount(addressDetailsData.received_usd)
    : "";
  const receivedAsset = addressDetailsData.received
    ? getTxAmount(addressDetailsData.received, balancePrecision, viewPrecision)
    : "";

  const spentUsd = addressDetailsData.sent_usd
    ? getUsdAmount(addressDetailsData.sent_usd)
    : "";
  const spentAsset = addressDetailsData.sent
    ? getTxAmount(addressDetailsData.sent, balancePrecision, viewPrecision)
    : "";

  const hasReceived = !!receivedUsd || !!receivedAsset;
  const hasSpent = !!spentUsd || !!spentAsset;

  if (!balanceUsd && !balanceAsset && !hasReceived && !hasSpent) {
    return null;
  }

  return (
    <div className={styles.balance}>
      <div className={styles.title}>Balance</div>
      {hasBalanceInfo() && (
        <>
          {balanceUsd && <div className={styles.value}>{balanceUsd}</div>}
          {balanceAsset && (
            <div className={styles.crypto}>
              {balanceAsset} {asset}
            </div>
          )}
          {(hasReceived || hasSpent) && (
            <table className={styles.list}>
              <tbody>
                {hasReceived && (
                  <tr>
                    <td>Total Received</td>
                    <td>
                      {receivedUsd && (
                        <>
                          <span>{receivedUsd}</span>&nbsp;•&nbsp;
                        </>
                      )}
                      {receivedAsset && (
                        <span>
                          {receivedAsset} {asset}
                        </span>
                      )}
                    </td>
                  </tr>
                )}

                {hasSpent && (
                  <tr>
                    <td>Total Sent</td>
                    <td>
                      {spentUsd && (
                        <>
                          <span>{spentUsd}</span>&nbsp;•&nbsp;
                        </>
                      )}
                      {spentAsset && (
                        <span>
                          {spentAsset} {asset}
                        </span>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

export default BalanceInfo;
